<template>
  <div class="analysis-section">
    <!-- 正在分析的过程（loading状态） -->
    <div class="analysis-item loading-item" v-if="isLoadingLocal">
      <div class="analysis-header">
        <div class="analysis-icon" style="background-color: #333333;">
          <img src="../assets/images/progess.png" alt="progress-icon" />
        </div>
        <h4>分析过程</h4>
        <el-button type="text" size="small" class="expand-btn" @click="toggleExpand()">
          {{ isExpanded() ? '收起' : '展开' }}
          <el-icon v-if="isExpanded()"><ArrowUp /></el-icon>
          <el-icon v-else><ArrowDown /></el-icon>
        </el-button>
      </div>
      <div class="analysis-content" v-if="isExpanded()">
        <div class="analysis-icon">
          <el-icon class="loading-icon"><Loading /></el-icon>
        </div>
        <div>
          <div class="loading-text" style="color: #0093FF;font-size: 15px;">正在检索相关专利以辅助构建分析逻辑...</div>
          <div class="loading-text" style="color: #858482;font-size: 14px;">结合类似技术案例生成推理链</div>
          <div class="loading-text" style="color: #a0a09c;font-size: 13px;margin-top: 4px;">预计等待时间 2-3 分钟</div>
        </div>
      </div>
    </div>

    <!-- 已完成的分析过程 -->
    <div class="analysis-item" v-for="(item, index) in mergedAnalysisResults" :key="index">
      <div class="analysis-header">
        <div class="analysis-icon" style="background-color: #333333;">
          <img src="../assets/images/progess.png" alt="progress-icon" />
        </div>
        <h4>分析过程</h4>
        <el-button type="text" size="small" class="expand-btn" @click="toggleExpand(index)">
          {{ isExpanded(index) ? '折叠' : '展开' }}
          <el-icon v-if="isExpanded(index)"><ArrowUp /></el-icon>
          <el-icon v-else><ArrowDown /></el-icon>
        </el-button>
      </div>
      <div class="analysis-content" v-show="isExpanded(index)">
        <!-- 技术路径分析项 -->
         <div class="analysis-subtitle">精准度 AN-12 分析：{{ props.analysisContent }}</div>
        <p>解决思路：</p>
        <div 
          v-for="(techItem, techIndex) in (item.techPaths || [])" 
          :key="techIndex"
          class="tech-path-item"
        >
          <div class="tech-path-header">
           <div style="background-color: #A0D5FB;width: 14px;height: 14px;border-radius: 50%;;"></div>
            <h5>{{ techItem.title }}</h5>
          </div>
          <div class="tech-path-content">
            <MarkdownRenderer 
              :content="techItem.content.join('\n')" 
              :enable-typing="false"
              @typing-complete="handleTypingComplete(index, techIndex)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed, onMounted } from 'vue'
import {
  ArrowDown,
  ArrowUp,
  Document,
  Loading,
  Close,
  CircleCheck,
  ChatLineSquare
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
// 引入showdown.js解析库
import Showdown from 'showdown'
// 引入MarkdownRenderer组件
import MarkdownRenderer from './MarkdownRenderer.vue'
// 引入项目编辑接口
import { projectEdit } from '@/api/process'

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false
  },
  progress: {
    type: Number,
    default: 0
  },
  remainingTime: {
    type: Number,
    default: 0
  },
  analysisResults: {
    type: Array,
    default: () => []
  },
  currentStreamingIndex: {
    type: Number,
    default: -1
  },
  // 新增：接收要分析的内容
  analysisContent: {
    type: String,
    default: ''
  },
  // 新增：控制收起状态
  collapse: {
    type: Boolean,
    default: false
  },
  // 新增：接收用于数据复现的分析数据
  reproductionData: {
    type: Object,
    default: null
  },
  // 新增：项目ID，用于分析完成后上传数据
  projectId: {
    type: [String, Number],
    default: null
  },
  isReading: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['cancel', 'analysis-complete'])

// 初始化Showdown转换器
const converter = new Showdown.Converter()

// 展开/折叠状态管理
const expandedItems = ref({main: true})
// 防止重复调用标志
const isGenerating = ref(false)
// 本地loading状态
const isLoadingLocal = ref(false)
// 本地存储分析结果，避免完全依赖props
const localAnalysisResults = ref([])

// 跟踪每个分析结果中每个技术路径的打字完成状态
const typingCompleteStatus = ref({})

// 合并分析结果，优先使用本地结果
const mergedAnalysisResults = computed(() => {
  if (localAnalysisResults.value.length > 0) {
    return localAnalysisResults.value
  }
  return props.analysisResults
})




// 调用API生成分析过程（非流式输出）
const generateAnalysis = async () => {
  console.log('开始生成分析', props.analysisContent)
  if (!props.analysisContent) {
    console.error('分析内容不能为空')
    return
  }
  
  // 防止重复调用
  if (isGenerating.value) {
    console.log('分析已在进行中，请勿重复调用')
    return
  }
  
  isGenerating.value = true
  isLoadingLocal.value = true

  // 200 秒超时控制：模型/服务异常时不再无限等待（避免界面"卡死"无反馈）
  const controller = new AbortController()
  const timeoutTimer = setTimeout(() => controller.abort(), 200 * 1000)

  // 模型（deepseek-v4-flash-0731）偶发返回空内容：最多尝试 3 次（1 次正式 + 2 次自动重试），
  // 空返回/请求失败时自动重试，避免空结果直接中断流程导致页面卡死。
  const MAX_ATTEMPTS = 3
  let lastError = null

  try {
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      if (attempt > 1) {
        ElMessage.warning(`模型本次返回空结果，自动重试（${attempt - 1}/${MAX_ATTEMPTS - 1}）...`)
      }
      try {
        // 发起普通JSON请求
        const response = await fetch('/api/process/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ content: props.analysisContent }),
          signal: controller.signal
        })

        console.log('响应状态:', response.status)
        if (!response.ok) {
          throw new Error(`API请求失败，状态码: ${response.status}`)
        }

        // 解析JSON响应
        const responseData = await response.json()
        console.log('解析后的响应数据:', responseData)

        const fullContent = responseData.result || ''
        console.log('完整内容:', fullContent)

        // 模型偶发空返回：不算失败，继续重试
        if (!fullContent.trim()) {
          console.warn(`第 ${attempt} 次尝试：模型返回空内容`)
          lastError = new Error('模型返回空内容')
          continue
        }

        // 解析Markdown内容
        const parsedResult = parseMarkdownResult(fullContent)
        console.log('解析后的Markdown结果:', parsedResult)

        // 创建一个新的分析结果
        const analysisResult = {
          techPaths: parsedResult.techPaths,
          isStreaming: false,
          fullContent: fullContent
        }

        // 即使模型没有严格按约定输出数字标题，也保留原始结果，避免页面只显示空的“解决思路”。
        if (analysisResult.techPaths.length === 0) {
          analysisResult.techPaths = [{
            title: '分析结果',
            content: [fullContent.trim()]
          }]
          console.warn('Markdown未解析出数字章节，已使用原始内容降级展示')
        }

        // 存储到本地，确保页面能显示结果
        localAnalysisResults.value = [analysisResult]
        console.log('分析结果已存储到本地', localAnalysisResults.value)

        // 不立即通知父组件分析完成，而是等待所有打字效果完成后通知父组件
        // （enable-typing=false 时 MarkdownRenderer 挂载即触发 typing-complete）
        console.log('分析结果已生成，等待打字效果完成后通知父组件', analysisResult)
        return
      } catch (error) {
        // 超时不重试，直接跳出
        if (error.name === 'AbortError') {
          lastError = error
          break
        }
        console.warn(`第 ${attempt} 次尝试失败:`, error)
        lastError = error
      }
    }

    // 所有尝试都失败/空内容：明确提示用户并结束流程，避免父页面一直等待
    console.error('生成分析过程失败:', lastError)
    if (lastError?.name === 'AbortError') {
      ElMessage.error('分析超时（200秒），请重试')
    } else {
      ElMessage.error('分析失败：模型多次未产出有效内容，请重试')
    }
    emit('analysis-complete', {
      techPaths: [],
      isStreaming: false,
      fullContent: ''
    })
  } finally {
    isGenerating.value = false
    isLoadingLocal.value = false
  }
}

// 解析Markdown格式的分析结果
const parseMarkdownResult = (markdown) => {
  // 关键修复：先预处理，在每个 ## 前插入换行符
  // 确保每个标题都单独成行
  console.log('原始Markdown:', markdown)
  let processedMarkdown = markdown
    .replace(/(^|\s)(##\s+\d+\.)/g, '\n$2')  // 在 ## 标题前插入换行
    .replace(/(^|\s)(#\s+[^#\n])/g, '\n$2') // 在 # 标题前插入换行
    .trim()
  
  const lines = processedMarkdown.split('\n')
  const techPaths = []
  let currentTitle = ''
  let currentContent = [] 
  
  lines.forEach((line) => {
    // 匹配二级标题（## 1. xxx）
    const titleMatch = line.match(/^\s*##\s+(\d+\.\s*[^\n]+)/)
    
    if (titleMatch) {
      if (currentTitle) {
        techPaths.push({
          title: currentTitle,
          content: currentContent
        })
        currentContent = []
      }
      currentTitle = titleMatch[1].trim()
    } else if (currentTitle) {
      // 跳过一级标题（# xxx），不加入内容
      if (!line.match(/^\s*#\s+/)) {
        const trimmed = line.trim()
        // 纯分隔线直接跳过：避免结尾 "---" 被 Showdown 解析成 setext 标题，且 hr 本身样式已隐藏
        if (trimmed === '---') return
        // 保留空行（加入空字符串），保证 Markdown 表格/列表块结构完整；
        // 否则 join('\n') 后缺少空行分隔，Showdown 会把表格行并入列表、把文字吸入表格
        currentContent.push(trimmed === '' ? '' : trimmed)
      }
    }
  })
  
  // 保存最后一个章节（去掉结尾多余空行）
  if (currentTitle) {
    while (currentContent.length > 0 && currentContent[currentContent.length - 1] === '') {
      currentContent.pop()
    }
    techPaths.push({
      title: currentTitle,
      content: currentContent
    })
  }
  
  return {
    techPaths: techPaths,
    isStreaming: false
  }
}

// 切换展开/折叠状态
const toggleExpand = (index = null) => {
  if (index === null) {
    // 切换整个分析过程的展开状态
    expandedItems.value.main = !expandedItems.value.main
  } else {
    // 切换单个分析结果的展开状态
    // 如果是第一次点击，初始状态是true，所以要设置为false
    if (expandedItems.value[index] === undefined) {
      expandedItems.value[index] = false
    } else {
      expandedItems.value[index] = !expandedItems.value[index]
    }
  }
}

// 处理打字完成事件
const handleTypingComplete = (analysisIndex, techIndex) => {
  // 标记当前技术路径打字完成
  if (!typingCompleteStatus.value[analysisIndex]) {
    typingCompleteStatus.value[analysisIndex] = {}
  }
  typingCompleteStatus.value[analysisIndex][techIndex] = true
  
  // 强制Vue重新渲染，以更新下一个技术路径的enableTyping状态
  // 使用Vue的响应式API确保状态变化被检测到
  typingCompleteStatus.value = { ...typingCompleteStatus.value }
  
  // 启动下一个技术路径的打字
  const currentAnalysis = mergedAnalysisResults.value[analysisIndex]
  if (currentAnalysis && currentAnalysis.techPaths && techIndex < currentAnalysis.techPaths.length - 1) {
    // 下一个技术路径的索引
    const nextTechIndex = techIndex + 1
    console.log(`当前技术路径 ${techIndex} 完成，准备启动下一个技术路径 ${nextTechIndex}`)
  }
  
  // 检查当前分析结果的所有技术路径是否都已完成打字
  const isAllTechPathsComplete = currentAnalysis && currentAnalysis.techPaths && currentAnalysis.techPaths.every((_, index) => {
    return typingCompleteStatus.value[analysisIndex] && typingCompleteStatus.value[analysisIndex][index]
  })
  
  // 如果所有技术路径都已完成打字，通知父组件分析完成
  if (isAllTechPathsComplete) {
    console.log(`分析结果 ${analysisIndex} 的所有技术路径打字完成，通知父组件`)
    emit('analysis-complete', currentAnalysis)
  }
}

// 检查是否应该启用某个技术路径的打字效果
const shouldEnableTyping = (analysisIndex, techIndex) => {
  // 第一个技术路径总是可以开始打字
  if (techIndex === 0) {
    return true
  }
  
  // 检查前一个技术路径是否已经打字完成
  if (typingCompleteStatus.value[analysisIndex] && typingCompleteStatus.value[analysisIndex][techIndex - 1]) {
    return true
  }
  
  return false
}

// 检查是否展开
const isExpanded = (index = null) => {
  if (index === null) {
    // 直接返回expandedItems.value.main，默认为true
    return expandedItems.value.main === undefined ? true : expandedItems.value.main
  }
  // 直接返回expandedItems.value[index]，默认为true
  return expandedItems.value[index] === undefined ? true : expandedItems.value[index]
}

// 组件挂载时检查是否需要生成分析
onMounted(() => {
})


// // 监听分析状态变化，触发分析过程
// watch(
//   () => [props.isLoading, props.currentStreamingIndex, mergedAnalysisResults],
//   ([newIsLoading, newCurrentStreamingIndex, newAnalysisResults]) => {
//     // 当isLoading为true时，触发分析过程
//     if (newIsLoading) {
//       console.log('props.isLoading为true，触发分析过程')
//       isLoadingLocal.value = true
//       generateAnalysis()
//     }
//     // 当分析全部完毕时，可以在这里添加其他逻辑
//     // 不再自动收起所有分析结果，让用户手动控制
//   },
//   { deep: true }
// )

// 监听collapse prop变化，自动收起分析过程
watch(
  () => props.collapse,
  (newCollapse) => {
    if (newCollapse) {
      console.log('自动收起分析过程')
      // 收起主分析过程
      expandedItems.value.main = false
      
      // 收起所有已完成的分析过程
      props.analysisResults.forEach((_, index) => {
        expandedItems.value[index] = false
      })
      
      console.log('分析过程已自动收起', expandedItems.value)
    }
  }
)
// watch(
//   () => props.reproductionData,
//   (newReproductionData) => {
//     console.log('reproductionData变化:', newReproductionData)
//     if (newReproductionData && newReproductionData.analysisResults && Array.isArray(newReproductionData.analysisResults)) {
//       isLoadingLocal.value = false
//     } else {
//       console.log(1)
//       isLoadingLocal.value = true
//       generateAnalysis()
//     }
//   },
//   { deep: true }
// )
// watch(
//   () => props.isLoading,
//   (newIsLoading) => {
//     console.log('isLoading变化:', newIsLoading)
//     if (newIsLoading) {
//       isLoadingLocal.value = true
//       generateAnalysis()
//     } 
//   }
// )
watch(
  () => props.isReading,
  (newIsReading) => {
    console.log('isReading变化:', newIsReading)
    if (newIsReading == 2) {
      // 只有明确标记"需要分析"(2)才触发；1=已恢复数据停止，0=初始态不触发
      isLoadingLocal.value = true
      generateAnalysis()
    } else {
      isLoadingLocal.value = false
    }
  }
)
</script>

<style scoped lang="scss">
/* 分析过程区域 */
.analysis-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.analysis-item {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.analysis-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  .analysis-icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f0f9ff;
    border-radius: 4px;
    
    
  }
  
  h4 {
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.analysis-content {
  padding-left: 40px;
  .loading-icon {
      animation: spin 1.5s linear infinite;
    }
}

/* Loading状态样式 */
.loading-item .analysis-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
}

.loading-text {
  font-size: 14px;
  color: #666;
}

.progress-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  color: #666;
  
  :deep(.el-progress) {
    width: 100%;
  }
}

.cancel-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ff4d4f;
  cursor: pointer;
  font-size: 14px;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ff7875;
  }
}

/* 分析结果样式 */
.analysis-subtitle {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.analysis-content p {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 16px;
}

/* 技术路径分析项 */
.tech-path-item {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
  
  &.streaming {
    animation: fadeIn 0.5s ease;
    border-color: #90caf9;
    background-color: #f0f9ff;
  }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.tech-path-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  
  :deep(.el-icon) {
    color: #67c23a;
  }
  
  h5 {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.tech-path-content {
  padding-left: 28px;
  
  p {
    font-size: 13px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 8px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.expand-btn {
  color: #666;
  
  :deep(.el-icon) {
    margin-left: 4px;
  }
}
</style>